import { useState, useRef, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Send, MoreVertical, Heart, Loader2 } from "lucide-react";
import { AppShell } from "../components/AppShell";
import { cn } from "../lib/utils";
import { useMessages, useSendMessage } from "../hooks/useDating";
import { useProfileMe } from "../hooks/useProfile";

export function Chat() {
  const { id } = useParams<{ id: string }>();
  const matchId = id ?? "";
  const { data: me } = useProfileMe();
  const { data: messages = [], isLoading } = useMessages(matchId);
  const send = useSendMessage(matchId);
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  async function handleSend() {
    const body = draft.trim();
    if (!body) return;
    setDraft("");
    try {
      await send.mutateAsync(body);
    } catch {
      setDraft(body);
    }
  }

  return (
    <AppShell hideNav>
      <div className="flex min-h-screen flex-col">
        <div className="sticky top-[57px] z-20 border-b border-border bg-white/90 backdrop-blur-xl">
          <div className="container flex max-w-2xl items-center gap-3 py-3">
            <Link
              to="/matches"
              aria-label="Zurück"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-surface"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full bg-brand-gradient">
                <span className="absolute inset-0 flex items-center justify-center">
                  <Heart className="h-5 w-5 text-white" fill="currentColor" />
                </span>
              </div>
              <div className="min-w-0">
                <p className="truncate text-base font-bold text-ink-900">
                  Chat
                </p>
                <p className="text-[11px] text-ink-500">Match #{matchId.slice(0, 8)}</p>
              </div>
            </div>
            <button
              type="button"
              aria-label="Mehr"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink-500 hover:bg-surface"
            >
              <MoreVertical className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-6">
          <div className="mx-auto flex max-w-2xl flex-col gap-2">
            {isLoading && (
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-brand-500" />
            )}
            {messages.map((m) => {
              const mine = m.senderId === me?.id;
              const time = new Date(m.createdAt).toLocaleTimeString("de-DE", {
                hour: "2-digit",
                minute: "2-digit",
              });
              return (
                <div
                  key={m.id}
                  className={cn(
                    "max-w-[75%] rounded-3xl px-4 py-2.5",
                    mine
                      ? "self-end bg-brand-gradient text-white rounded-br-md shadow-glow"
                      : "self-start bg-white text-ink-900 shadow-soft rounded-bl-md",
                  )}
                >
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {m.body}
                  </p>
                  <p
                    className={cn(
                      "mt-1 text-[10px] tabular-nums",
                      mine ? "text-white/80" : "text-ink-300",
                    )}
                  >
                    {time}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="sticky bottom-0 border-t border-border bg-white/90 backdrop-blur-xl"
        >
          <div className="container flex max-w-2xl items-end gap-2 py-3">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Nachricht schreiben…"
              rows={1}
              maxLength={2000}
              className="input-field max-h-32 resize-none py-2.5"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <button
              type="submit"
              disabled={!draft.trim() || send.isPending}
              aria-label="Senden"
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-gradient text-white shadow-glow transition active:scale-95 disabled:opacity-40 disabled:shadow-none"
            >
              {send.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </button>
          </div>
        </form>
      </div>
    </AppShell>
  );
}
